import { useState } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, post, put } from '../api'
import { Button, Notice } from '../components/ui'
import { FactorBadge, FactorCancelled, SecondFactorProvider, useSecondFactor } from './SecondFactor'
import { nodePath, type RelayRole } from './network/api'
import { Copy, Empty, ErrorBox, Fact, NetPanel, ROLE_LABEL, RoleGlyph, SkeletonRows } from './network/ui'
import {
  CAPTIONS_LABEL, TYPE_LABEL, fetchLicense, licenseDay, licenseKey, loadErrorText, peekLicense, reasonText, recentMonths, stateLook,
  type LicenseInfo, type LicenseNode, type LicenseView,
} from './licenseApi'

/*
 * Admin › License (PLAT-LIC-1, docs/platform/licensing.md §6): the license of
 * this installation, its seats, the month's usage, the nodes and their seats,
 * loading a new key. Nothing here can stop a broadcast: Standby only stops new
 * work on a node, a new key only changes what may start.
 */

const TONE = { live: 'border-live/60 text-live', warn: 'border-warn/60 text-warn', fail: 'border-fail/60 text-fail', muted: 'border-line-2 text-text-soft' }

export function AdminLicense() {
  return (
    <SecondFactorProvider>
      <LicensePage />
    </SecondFactorProvider>
  )
}

function LicensePage() {
  const [month, setMonth] = useState('')
  const q = useQuery({ queryKey: licenseKey(month), queryFn: () => fetchLicense(month), refetchInterval: 60_000, retry: false })
  const [upload, setUpload] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'error' | 'info'; text: string } | null>(null)

  if (q.error) {
    const gone = q.error instanceof ApiError && q.error.status === 404
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-medium">License</h1>
        {gone ? <Notice kind="info">This center has no license API (an older version).</Notice> : <ErrorBox text={(q.error as Error).message} onRetry={() => void q.refetch()} busy={q.isFetching} testId="license-error" />}
      </div>
    )
  }
  const v = q.data
  return (
    <div className="space-y-6" data-testid="license-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-medium">License</h1>
        <span className="ml-auto flex items-center gap-2">
          <FactorBadge />
          <Button variant={v?.license ? 'plain' : 'primary'} onClick={() => setUpload((o) => !o)} data-testid="license-upload-open" aria-expanded={upload}>
            {v?.license ? 'Upload license' : 'Paste a license key'}
          </Button>
        </span>
      </div>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {!v ? (
        <SkeletonRows rows={4} cols={4} />
      ) : (
        <>
          {v.mode === 'observe' && (
            <p role="status" data-testid="license-observe" className="rounded-md border border-line-2 px-3 py-2 text-sm text-text-soft">
              <span className="mr-2 font-mono text-[11px] font-medium tracking-[.06em] text-text">OBSERVE MODE</span>
              This version shows seats and limits but refuses nothing yet
              {v.refusals.would_refuse_streams > 0 && (
                <>
                  {' '}
                  — it would have refused <span className="tabular" data-testid="license-would-refuse">{v.refusals.would_refuse_streams}</span> new {v.refusals.would_refuse_streams === 1 ? 'stream' : 'streams'}
                </>
              )}
              .{v.dev_build && ' Development build: development keys are accepted.'}
            </p>
          )}
          {upload && (
            <UploadLicense
              current={v}
              onDone={(text) => {
                setUpload(false)
                setMsg({ kind: 'info', text })
              }}
              onCancel={() => setUpload(false)}
            />
          )}
          {v.license ? <Summary v={v} /> : <NoLicense v={v} onPaste={() => setUpload(true)} />}
          <Cards v={v} month={month} setMonth={setMonth} />
          <Nodes v={v} onMsg={setMsg} />
          <Installation v={v} />
        </>
      )}
    </div>
  )
}

function StateBadge({ v }: { v: LicenseView }) {
  const look = stateLook(v.status)
  return (
    <span data-testid="license-state" data-state={v.status.state} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono text-[11px] font-medium tracking-[.06em] uppercase ${TONE[look.tone]}`}>
      <span aria-hidden>{look.glyph}</span>
      {look.word}
    </span>
  )
}

function Summary({ v }: { v: LicenseView }) {
  const l = v.license as LicenseInfo
  const s = v.status
  const reason = s.state === 'restricted' || s.state === 'grace' ? reasonText(s, l) : ''
  return (
    <NetPanel
      testId="license-summary"
      title={
        <div className="flex flex-wrap items-center gap-3">
          <StateBadge v={v} />
          <h2 className="text-ui-title font-medium" data-testid="license-type">{TYPE_LABEL[l.type] ?? l.type}</h2>
          <span className="text-sm text-text-soft" data-testid="license-customer">{l.customer}</span>
        </div>
      }
      aside={<Copy value={l.license_id} label="license ID" testId="license-id" />}
    >
      {reason && (
        <p className={`mb-4 text-sm ${s.state === 'restricted' ? 'text-fail' : 'text-warn'}`} data-testid="license-reason">
          {s.state === 'restricted' ? `License inactive: ${reason}. Running streams continue, new streams are refused.` : `License expired on ${licenseDay(s.expires_at)}. New nodes can't get a seat. On ${licenseDay(s.grace_until)} new streams will stop being accepted; running streams are not affected.`}
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Fact k="Expires" testId="license-expires">
          {l.expires_at ? (
            <>
              {licenseDay(l.expires_at)}
              {s.days_left ? <span className="text-muted"> · {s.days_left} {s.days_left === 1 ? 'day' : 'days'}</span> : null}
            </>
          ) : (
            'Never'
          )}
        </Fact>
        <Fact k="Updates until" testId="license-updates">{l.updates_until ? licenseDay(l.updates_until + 'T00:00:00Z') : '—'}</Fact>
        <Fact k="Activation">{l.activation === 'offline' ? 'Offline key' : 'Online'}</Fact>
        <Fact k="Grace after expiry">{l.grace_days} days</Fact>
        <Fact k="Loaded from">{v.source === 'file' ? 'LICENSE_FILE' : 'Console'}</Fact>
        {l.note && <Fact k="Note" className="col-span-2">{l.note}</Fact>}
      </div>
    </NetPanel>
  )
}

function NoLicense({ v, onPaste }: { v: LicenseView; onPaste: () => void }) {
  return (
    <Empty
      testId="license-none"
      title="No license"
      action={
        <Button variant="primary" onClick={onPaste} data-testid="license-paste">
          Paste a license key
        </Button>
      }
    >
      <p>
        Paste a license key, or put it in the file named by <span className="font-mono">LICENSE_FILE</span>. To get a key or a 30-day trial, send this installation’s fingerprint to Strevix:
      </p>
      <p className="mt-2">
        <Copy value={v.fingerprint} label="fingerprint" testId="license-none-fp" />
      </p>
    </Empty>
  )
}

function Card({ k, value, sub, testId, tone }: { k: string; value: string; sub?: string; testId: string; tone?: 'warn' }) {
  return (
    <div className={`bg-panel min-w-0 rounded-lg border px-4 py-3 ${tone === 'warn' ? 'border-warn/50' : 'border-line'}`} data-testid={testId}>
      <p className="mono-label uppercase">{k}</p>
      <p className={`tabular mt-1 text-2xl font-medium ${tone === 'warn' ? 'text-warn' : ''}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  )
}

function Cards({ v, month, setMonth }: { v: LicenseView; month: string; setMonth: (m: string) => void }) {
  const hourly = v.license?.type === 'hourly'
  const capLimit = v.captions.limit
  const months = recentMonths()
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-ui-title font-medium">Usage</h2>
        <label className="ml-auto flex items-center gap-2 text-sm text-text-soft">
          Month
          <select
            className="rounded-md border border-line bg-panel px-2 py-1 text-sm text-text"
            value={month || months[0]}
            onChange={(e) => setMonth(e.target.value === months[0] ? '' : e.target.value)}
            data-testid="license-month"
          >
            {months.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        <a href={`/api/admin/license/usage.csv${month ? `?month=${month}` : ''}`} className="text-sm text-text-soft underline underline-offset-2 hover:text-text" data-testid="license-csv" download>
          Export CSV
        </a>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card k="Seats in use" testId="license-seats" value={v.seats.unlimited ? `${v.seats.used}` : `${v.seats.used} of ${v.seats.limit}`} sub={v.seats.unlimited ? 'Unlimited' : `${Math.max(0, v.seats.limit - v.seats.used)} free`} />
        {hourly ? (
          <Card k={`Node-hours · ${v.usage.month}`} testId="license-node-hours" value={String(v.usage.node_hours)} />
        ) : (
          <Card k={`Peak · ${v.usage.month}`} testId="license-peak" value={String(v.usage.peak)} sub={`Node-hours ${v.usage.node_hours}`} />
        )}
        <Card k="Waiting for a seat" testId="license-waiting" value={String(v.seats.waiting)} sub={v.seats.standby ? `${v.seats.standby} on standby` : undefined} tone={v.seats.waiting > 0 ? 'warn' : undefined} />
        <Card
          k="Captions workers"
          testId="license-captions"
          value={capLimit < 0 ? `${v.captions.licensed.length}` : `${v.captions.licensed.length} of ${capLimit}`}
          sub={v.license ? CAPTIONS_LABEL[v.license.captions] : undefined}
        />
      </div>
      <p className="text-xs text-muted">
        A node counts toward the month’s peak once it has run 60 minutes in the month with a seat. Node-hours round up per node.
      </p>
    </section>
  )
}

const SEAT_TEXT: Record<LicenseNode['seat'], string> = { licensed: 'Licensed', waiting: 'Waiting for a seat', standby: 'Standby', none: 'No seat' }
const SEAT_TONE: Record<LicenseNode['seat'], string> = { licensed: 'text-live', waiting: 'text-warn', standby: 'text-text-soft', none: 'text-muted' }
const SEAT_GLYPH: Record<LicenseNode['seat'], string> = { licensed: '●', waiting: '▲', standby: '─', none: '○' }

const hm = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`)

function Nodes({ v, onMsg }: { v: LicenseView; onMsg: (m: { kind: 'error' | 'info'; text: string } | null) => void }) {
  const qc = useQueryClient()
  const [ask, setAsk] = useState<{ id: string; seat: 'standby' | 'licensed' } | null>(null)
  const [busy, setBusy] = useState(false)
  const apply = async (id: string, seat: 'standby' | 'licensed') => {
    setBusy(true)
    onMsg(null)
    try {
      await post(`/api/admin/nodes/${encodeURIComponent(id)}/license-seat`, { seat })
      setAsk(null)
      await qc.invalidateQueries({ queryKey: ['admin-license'] })
    } catch (e) {
      const free = e instanceof ApiError && e.code === 'no_free_seat'
      onMsg({ kind: 'error', text: free ? 'No free license seat: put another node on standby or load a larger license.' : (e as Error).message })
    } finally {
      setBusy(false)
    }
  }
  return (
    <NetPanel title="Nodes" pad={false} testId="license-nodes">
      {v.nodes.length === 0 ? (
        <p className="p-4 text-sm text-muted">No media nodes yet. Integrations nodes (chats only) never use a seat.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal w-full min-w-[720px]">
            <thead>
              <tr>
                <th className="px-4">Node</th>
                <th className="px-4">Roles</th>
                <th className="px-4">Seat</th>
                <th className="px-4">This month</th>
                <th className="px-4">Counted</th>
                <th className="px-4" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {v.nodes.map((n) => (
                <tr key={n.id} data-testid={`license-node-${n.id}`} data-seat={n.seat}>
                  <td className="px-4 py-1.5">
                    <Link to={nodePath(n.id)} className="font-medium underline-offset-2 hover:underline">{n.name || n.id}</Link>
                    <span className="ml-2 text-xs text-muted">{n.online ? 'online' : 'offline'}</span>
                  </td>
                  <td className="px-4 py-1.5">
                    <span className="flex flex-wrap gap-2 text-xs text-text-soft">
                      {n.roles.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1" title={ROLE_LABEL[r as RelayRole] ?? r}>
                          <RoleGlyph role={r as RelayRole} />
                          {ROLE_LABEL[r as RelayRole] ?? r}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className={`px-4 py-1.5 font-mono text-[11px] font-medium tracking-[.06em] whitespace-nowrap ${SEAT_TONE[n.seat]}`} data-testid={`license-seat-${n.id}`}>
                    <span aria-hidden>{SEAT_GLYPH[n.seat]}</span> {SEAT_TEXT[n.seat]}
                  </td>
                  <td className="tabular px-4 py-1.5">{n.minutes ? hm(n.minutes) : '—'}</td>
                  <td className="px-4 py-1.5">{n.counted ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-1.5 text-right whitespace-nowrap">
                    {ask?.id === n.id ? (
                      <span className="inline-flex flex-wrap items-center justify-end gap-2" data-testid={`license-ask-${n.id}`}>
                        <span className="text-xs text-text-soft">
                          {ask.seat === 'standby' ? 'No new streams on this node; running ones continue.' : 'Takes a free seat.'}
                        </span>
                        <Button variant={ask.seat === 'standby' ? 'danger' : 'primary'} disabled={busy} onClick={() => void apply(n.id, ask.seat)} data-testid={`license-confirm-${n.id}`}>
                          {ask.seat === 'standby' ? 'Put on standby' : 'License'}
                        </Button>
                        <Button variant="quiet" onClick={() => setAsk(null)}>Cancel</Button>
                      </span>
                    ) : n.seat === 'licensed' ? (
                      <Button variant="quiet" onClick={() => setAsk({ id: n.id, seat: 'standby' })} data-testid={`license-standby-${n.id}`}>Standby</Button>
                    ) : (
                      <Button
                        variant="quiet"
                        disabled={!v.status.new_seats || (!v.seats.unlimited && v.seats.used >= v.seats.limit)}
                        title={!v.status.new_seats ? 'The license gives no new seats now' : !v.seats.unlimited && v.seats.used >= v.seats.limit ? 'No free seat: put another node on standby or load a larger license' : undefined}
                        onClick={() => setAsk({ id: n.id, seat: 'licensed' })}
                        data-testid={`license-license-${n.id}`}
                      >
                        License
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </NetPanel>
  )
}

function Installation({ v }: { v: LicenseView }) {
  return (
    <NetPanel title="Installation" testId="license-installation">
      <div className="grid gap-4 sm:grid-cols-2">
        <Fact k="Fingerprint">
          <Copy value={v.fingerprint} label="fingerprint" testId="license-fp" />
        </Fact>
        <Fact k="Admin domain">{v.admin_domain || '—'}</Fact>
      </div>
      {v.clock.behind && (
        <p className="mt-3 text-sm text-warn" data-testid="license-clock">
          ▲ The system clock is behind the last known time ({new Date(v.clock.watermark).toISOString().replace('.000', '')}). License dates are counted from the last known time.
        </p>
      )}
      {v.load_errors?.length ? (
        <ul className="mt-3 space-y-1 text-sm text-warn" data-testid="license-load-errors">
          {v.load_errors.map((e) => (
            <li key={e}>▲ Not used: {e}</li>
          ))}
        </ul>
      ) : null}
      <p className="mt-3 text-xs text-muted">
        Nothing is sent to Strevix by this version: the key is checked on this server. The monthly journal (Export CSV) stays here for an audit.
      </p>
    </NetPanel>
  )
}

function UploadLicense({ current, onDone, onCancel }: { current: LicenseView; onDone: (text: string) => void; onCancel: () => void }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const [line, setLine] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const peek = line.trim() ? peekLicense(line) : null
  const cur = current.license
  const read = async (f: File) => setLine((await f.text()).trim())
  const apply = async () => {
    setBusy(true)
    setError('')
    try {
      await factor((totp) => put('/api/admin/license', { license: line.trim(), ...(totp ? { totp } : {}) }))
      await qc.invalidateQueries({ queryKey: ['admin-license'] })
      setLine('')
      onDone(peek ? `License ${peek.license_id} loaded.` : 'License loaded.')
    } catch (e) {
      if (e instanceof FactorCancelled) return
      setError(e instanceof ApiError ? loadErrorText(e.code, e.message) : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <NetPanel title="Load a license" testId="license-upload">
      <div className="space-y-3">
        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-text-soft">The license line (starts with STREVIX-LIC-1.)</span>
          <textarea
            className="min-h-24 w-full rounded-md border border-line bg-panel px-2 py-1.5 font-mono text-xs break-all"
            value={line}
            spellCheck={false}
            onChange={(e) => setLine(e.target.value)}
            data-testid="license-input"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-text-soft">
          or a file
          <input type="file" accept=".lic,.txt,text/plain" className="text-sm" data-testid="license-file" onChange={(e) => e.target.files?.[0] && void read(e.target.files[0])} />
        </label>
        {line.trim() && !peek && <p className="text-sm text-fail" data-testid="license-peek-bad">This does not look like a Strevix license.</p>}
        {peek && (
          <div className="rounded-md border border-line-2 px-3 py-2 text-sm" data-testid="license-peek">
            <p className="mb-1 text-xs text-muted">Read from the key (the center checks the signature on Apply):</p>
            <p>
              <span className="font-medium">{TYPE_LABEL[peek.type] ?? peek.type}</span> · {peek.customer} · {peek.expires_at ? `until ${licenseDay(peek.expires_at)}` : 'no end date'}
            </p>
            <p data-testid="license-peek-seats">
              Seats: {cur ? `${cur.max_nodes} → ` : ''}
              {peek.max_nodes || 'unlimited'}
              {cur && cur.type !== peek.type ? ` · type ${TYPE_LABEL[cur.type]} → ${TYPE_LABEL[peek.type] ?? peek.type}` : ''}
            </p>
            {cur && peek.max_nodes > 0 && peek.max_nodes < current.seats.used && (
              <p className="mt-1 text-warn" data-testid="license-peek-fewer">
                ▲ Fewer seats than nodes in use ({current.seats.used}). Nodes over the limit keep their running streams and take no new ones once those end.
              </p>
            )}
          </div>
        )}
        {error && <Notice>{error}</Notice>}
        <div className="flex gap-2">
          <Button variant="primary" disabled={busy || !peek} onClick={() => void apply()} data-testid="license-apply">
            {busy ? 'Checking…' : 'Apply'}
          </Button>
          <Button variant="quiet" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </NetPanel>
  )
}
